import { CdkDragDrop } from "@angular/cdk/drag-drop";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild
} from "@angular/core";

import { DocumentDuplicatePayload } from "@documentation/components/sidebar/tabs/documents-list/document-tree/document-context-menu/document-context-menu.component";
import {
  DocumentTreeComponent,
  DocumentTreeMode
} from "@documentation/components/sidebar/tabs/documents-list/document-tree/document-tree.component";
import { FolderContextMenuComponent } from "@documentation/components/sidebar/tabs/documents-list/document-tree/folder-node/folder-context-menu/folder-context-menu.component";
import { DocumentListItemModel } from "@documentation/models/document.model";
import { FolderModel, TreeNode } from "@documentation/models/folder.model";
import { CustomIconComponent } from "@shared/components/custom-icon/custom-icon.component";
import { ClickOrDoubleClickDirective } from "@shared/directives/click-or-double-click.directive";
import { injectParams } from "@shared/helpers/route.helper";

@Component({
  selector: "component-folder-node",
  imports: [
    CustomIconComponent,
    forwardRef(() => DocumentTreeComponent),
    FolderContextMenuComponent,
    ClickOrDoubleClickDirective
  ],
  templateUrl: "./folder-node.component.html",
  styleUrl: "./folder-node.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FolderNodeComponent {
  readonly item = input.required<FolderModel>();
  readonly folderIds = input.required<string[]>();
  readonly isPublicView = input.required<boolean>();
  readonly disableDrag = input.required<boolean>();
  readonly disableMove = input.required<boolean>();
  readonly expandAll = input.required<boolean>();
  readonly collapsed = input.required<boolean>();
  readonly isInPublicFolder = input.required<boolean>();
  readonly isCreateDisabled = input.required<boolean>();
  readonly hasContextMenu = input.required<boolean>();
  readonly allowPublicBadge = input.required<boolean>();
  readonly mode = input.required<keyof typeof DocumentTreeMode>();

  readonly selectedDocumentsMap = model<Map<string, DocumentListItemModel>>();

  readonly entityUpdated = output<TreeNode>();
  readonly documentDeleted = output<DocumentListItemModel>();
  readonly duplicateDocument = output<DocumentDuplicatePayload>();
  readonly folderDelete = output<FolderModel>();
  readonly drop = output<CdkDragDrop<TreeNode[]>>();
  readonly entityMoved = output<TreeNode>();
  readonly treeUpdated = output<void>();
  readonly documentCreate = output<FolderModel>();

  protected readonly isFolderCollapsed = signal<boolean>(true);

  protected readonly isPickerMode = computed(() => DocumentTreeMode[this.mode()] === DocumentTreeMode.Picker);
  protected readonly isDefaultMode = computed(() => DocumentTreeMode[this.mode()] === DocumentTreeMode.Default);
  protected readonly isChecked = computed(() => this.isCheckedComputation());
  protected readonly isIndeterminate = computed(() => this.isIndeterminateComputation());

  readonly tree = viewChild(DocumentTreeComponent);
  protected readonly cm = viewChild(FolderContextMenuComponent);

  private readonly _activeDocumentId = injectParams("id");

  constructor() {
    effect(() => this.isFolderCollapsed.set(!this.expandAll()));

    // Emits only distinct values
    const itemId = computed(() => this.item().id);
    // Note: this doesn't work with nested folders
    const content = computed(() => JSON.stringify(this.item().content.map(({ id }) => id)));

    effect(() => {
      // Extra triggers: re-run effect when expandAll, item ID, or content change.
      void this.expandAll();
      void itemId();
      void content();

      const activeDocumentId = this._activeDocumentId();

      untracked(() => {
        if (this.isFolderCollapsed() && activeDocumentId && !this.collapsed()) {
          this.isFolderCollapsed.set(!this.containsDocument(this.item(), activeDocumentId));
        }
      });
    });
  }

  private containsDocument(node: TreeNode, targetId: string): boolean {
    if (node.type === "document") {
      return node.id === targetId;
    }

    if (node.type === "folder" && node.content?.length) {
      return node.content.some((child) => this.containsDocument(child, targetId));
    }

    return false;
  }

  protected onFolderSingleClick(event: MouseEvent): void {
    if (!this.collapsed()) {
      this.isFolderCollapsed.set(!this.isFolderCollapsed());
    }
  }

  protected onFolderDoubleClick(event: MouseEvent): void {
    if (this.isDefaultMode()) {
      this.cm()?.onRenameClick(event);
    }
  }

  protected onToggleSelection(event: Event): void {
    const isEnabled = (event.target as HTMLInputElement).checked;

    const folder = this.item();
    const allDocs = this.collectAllDocuments(folder);

    const updated = new Map(this.selectedDocumentsMap());

    if (isEnabled) {
      allDocs.forEach((doc) => updated.set(doc.id, doc));
    } else {
      allDocs.forEach((doc) => updated.delete(doc.id));
    }

    this.selectedDocumentsMap.set(updated);
  }

  private isCheckedComputation(): boolean {
    const allDocs = this.collectAllDocuments(this.item());
    if (!allDocs.length) return false;

    const selected = this.selectedDocumentsMap();

    return allDocs.every((doc) => selected?.has(doc.id));
  }

  private isIndeterminateComputation(): boolean {
    const allDocs = this.collectAllDocuments(this.item());
    if (!allDocs.length) return false;

    const selected = this.selectedDocumentsMap();

    const count = allDocs.filter((doc) => selected?.has(doc.id)).length;

    return count > 0 && count < allDocs.length;
  }

  private collectAllDocuments(node: TreeNode): DocumentListItemModel[] {
    if (node.type === "document") return [node];

    let result: DocumentListItemModel[] = [];

    for (const item of node.content) {
      result = result.concat(this.collectAllDocuments(item));
    }

    return result;
  }
}
